// Merge tag chips: a click inserts the tag at the cursor in the field
// the agent was writing in, the subject or the body.
//
// The chip keeps the field's focus on press, so the insert lands where
// the caret was. A field the agent left keeps its caret too, so clicking
// a chip after tabbing away still fills the last field they wrote in. A
// hidden field (the subject on a text step) never takes the insert.
//
// A message editor (message_editor.js) takes the tag at its own caret:
// its named field is a hidden input, so the chip writes into the
// editable surface beside it and lets the editor read the body back.
//
// Targets: [data-placeholder-chips], with data-placeholder-targets naming
// the fields in fallback order. Names resolve inside the panel's own form,
// because several step editors can be open at once and each has a
// subject and a body.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

export function initPlaceholderChips(root = document) {
  root.querySelectorAll("[data-placeholder-chips]").forEach((panel) => {
    if (!firstBind(panel)) return;

    const form = panel.closest("form");
    if (!form) return;

    // An editor's named field is its hidden input; the surface is what
    // takes focus and the tag.
    const resolve = (name) => {
      const el = form.elements.namedItem(name);
      if (el?.matches?.("[data-message-input]")) {
        return el
          .closest("[data-message-editor]")
          ?.querySelector("[data-message-surface]");
      }

      return el;
    };
    const fields = () =>
      (panel.dataset.placeholderTargets || "")
        .split(" ")
        .map(resolve)
        .filter((el) => el && el.offsetParent !== null);

    let lastField = null;
    // A surface loses its selection when focus leaves, so its caret is
    // kept as it moves.
    let surfaceRange = null;
    const remember = (event) => {
      if (fields().includes(event.target)) lastField = event.target;
    };
    // Bound to the form rather than the document, so a swapped-out
    // editor takes its listener with it.
    form.addEventListener("focusin", remember);
    const onSelectionChange = () => {
      if (!panel.isConnected) {
        document.removeEventListener("selectionchange", onSelectionChange);
        return;
      }
      const selection = window.getSelection();
      if (
        lastField?.isContentEditable &&
        selection.rangeCount > 0 &&
        lastField.contains(selection.anchorNode)
      ) {
        surfaceRange = selection.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener("selectionchange", onSelectionChange);

    // Keeps focus, and so the caret, in the field being written.
    panel.addEventListener("mousedown", (event) => {
      if (event.target.closest("[data-placeholder-token]")) {
        event.preventDefault();
      }
    });

    panel.addEventListener("click", (event) => {
      const chip = event.target.closest("[data-placeholder-token]");
      if (!chip) return;

      const visible = fields();
      const written = visible.includes(lastField);
      const field = written ? lastField : visible[visible.length - 1];
      if (!field) return;

      const token = chip.dataset.placeholderToken;
      if (field.isContentEditable) {
        // A channel switch rebuilds the surface, leaving a kept range
        // pointing at nodes that are gone.
        const range =
          written && surfaceRange && field.contains(surfaceRange.startContainer)
            ? surfaceRange
            : null;
        insertIntoSurface(field, token, range);
        return;
      }

      // An untouched field has no caret worth keeping, so the tag goes
      // at the end rather than ahead of everything already there.
      const start = written ? field.selectionStart : field.value.length;
      const end = written ? field.selectionEnd : field.value.length;
      field.setRangeText(token, start, end, "end");
      field.focus();
      field.dispatchEvent(new Event("input", { bubbles: true }));
    });
  });
}

// insertIntoSurface types the tag at range, or at the end of the last
// line when there is none. insertText keeps it on the undo stack and
// fires the input event the editor reads the body back on.
function insertIntoSurface(surface, token, range) {
  surface.focus();
  const selection = window.getSelection();
  if (!range) {
    range = document.createRange();
    const last = surface.lastElementChild || surface;
    range.selectNodeContents(last);
    range.collapse(false);
  }
  selection.removeAllRanges();
  selection.addRange(range);
  document.execCommand("insertText", false, token);
}
